import { apiClient, ApiResponse } from './client';

export interface DashboardMyCollection {
  id: string;
  name: string;
  slug: string | null;
  image: string | null;
  count: number;
  floor: number;
  value: number;
  change24h_pct: number;
}

export interface DashboardExpiringOffer {
  id: string;
  nft: string;
  collection: string;
  offerPrice: number;
  floor: number;
  expiresMins: number;
  image: string | null;
  tokenId: string;
}

export interface DashboardListing {
  id: string;
  nft: string;
  collection: string;
  listPrice: number | null;
  floor: number;
  highestOffer: number | null;
  views: number | null;
  endingIn: string | null;
  image: string | null;
  tokenId: string;
}

export interface DashboardTopValuedNft {
  tokenId: string;
  name: string;
  collection: string;
  collectionSlug: string | null;
  image: string | null;
  value: number;
  basis: 'offer' | 'floor' | 'valuation';
  acquiredPrice: number | null;
  roi: number | null;
}

export type DashboardSinceVisitSignal =
  | { kind: 'new-listings'; count: number; detail: string; collectionId: string; slug: string | null }
  | { kind: 'floor-move'; change: number; direction: 'up' | 'down'; detail: string; collectionId: string; slug: string | null }
  | {
      kind: 'offer-received';
      count: number;
      collectionCount: number;
      topOfferPrice: number;
      topCollectionName: string | null;
      topFloor: number;
    }
  | { kind: 'offer-accepted'; detail: string; tokenId: string | null; role: 'buyer' | 'seller'; price: number }
  | { kind: 'market-top-sale'; price: number; detail: string; tokenId: string | null }
  | {
      kind: 'market-trending';
      salesCount: number;
      buyerCount: number;
      volume: number;
      detail: string;
      collectionId: string;
      slug: string | null;
    }
  | {
      kind: 'market-rising-floor';
      change: number;
      currentFloor: number;
      detail: string;
      collectionId: string;
      slug: string | null;
    }
  | {
      kind: 'market-pulse';
      salesCount: number;
      volume: number;
    };

export interface DashboardSinceVisit {
  since: string;
  items: DashboardSinceVisitSignal[];
}

export interface DashboardData {
  portfolio: {
    value: { xrp: number; usd: number | null; change24h_pct: number };
    balance: { xrp: number | null; usd: number | null; stale: boolean };
    counts: {
      buyOffers: number;
      sellOffers: number;
      endingSoon: number;
      soonestExpiryMins: number | null;
    };
  };
  collections: DashboardMyCollection[];
  expiringOffers: DashboardExpiringOffer[];
  /** Owned NFTs with at least one funded buy offer. Rich table layout. */
  buyOffersReceived: DashboardListing[];
  /** Top NFTs by estimated_value (liquidity-filtered). */
  topValuedNfts: DashboardTopValuedNft[];
  sinceVisit: DashboardSinceVisit;
}

export async function getDashboard(): Promise<DashboardData> {
  const response = await apiClient.get<ApiResponse<DashboardData>>('/me/dashboard');
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch dashboard');
  }
  return response.data;
}
